import React, { useEffect, useState } from "react";
import { FiExternalLink, FiMail, FiPhone, FiUser, FiX } from "react-icons/fi";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import useSWRImmutable from "swr/immutable";
import { get, post } from "../../../api";
import CountrySelector from "../../auth/components/CountrySelector";
import Checkbox from "../../cmp/form/Checkbox";
import FormGroup from "../../cmp/form/FormGroup";
import Input from "../../cmp/form/Input";
import PhoneInput from "../../cmp/form/phone/PhoneInput";
import Select from "../../cmp/form/Select";
import useToggle from "../../cmp/hooks/useToggle";
import Modal from "../../cmp/Modal";
import StyledButton from "../../cmp/themed/StyledButton";

function GiftContactInformation({
  data,
  onChange,
}: {
  data: any;
  onChange: (name: string) => (value: any) => void;
}) {
  const [visible, toggle] = useToggle();
  return (
    <>
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between">
          <div className="card-title">
            <FormattedMessage id="Contact Information" />
          </div>
          <button onClick={toggle} className="btn btn-link btn-sm">
            <FormattedMessage id="Edit" />
          </button>
        </div>
        <div className="d-flex align-items-center gap-2">
          <FiUser />
          <span>
            {data?.first_name} {data?.last_name}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <FiMail />
          <span>{data?.email}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <FiPhone />
          <span>{data?.phone}</span>
        </div>
      </div>
      <Modal
        visible={visible}
        title={<FormattedMessage id="Contact Information" />}
        onClose={toggle}
        onOk={toggle}
        okText={<FormattedMessage id="Submit" />}
      >
        <div className="row">
          <div className="col-md-6">
            <FormGroup label="First Name">
              <Input
                value={data?.first_name}
                onChange={onChange("first_name")}
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup label="Last Name">
              <Input value={data?.last_name} onChange={onChange("last_name")} />
            </FormGroup>
          </div>
        </div>
        <FormGroup label="Email address">
          <Input
            value={data?.email}
            onChange={onChange("email")}
            prefix={<FiMail />}
          />
        </FormGroup>
        <FormGroup label="Phone">
          <PhoneInput
            value={data?.phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange("phone")(e.target.value)
            }
          />
        </FormGroup>
      </Modal>
    </>
  );
}

function GiftShippingAddress({
  address,
  onChange,
}: {
  address: any;
  onChange: (name: string) => (value: any) => void;
}) {
  const [visible, toggle] = useToggle();
  return (
    <>
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between">
          <div className="card-title">
            <FormattedMessage id="Shipping Address" />
          </div>
          <button onClick={toggle} className="btn btn-link btn-sm">
            <FormattedMessage id="Edit" />
          </button>
        </div>
        <div>
          {address?.address1} {address?.address2}
          <br />
          {address?.city}, {address?.province}, {address?.zip}
          <br />
          {address?.country}
        </div>
      </div>
      <Modal
        visible={visible}
        onClose={toggle}
        title={<FormattedMessage id="Shipping Address" />}
      >
        <FormGroup label="Address Line 1" required>
          <Input value={address?.address1} onChange={onChange("address1")} />
        </FormGroup>
        <FormGroup label="Address Line 2">
          <Input value={address?.address2} onChange={onChange("address2")} />
        </FormGroup>
        <div className="row">
          <div className="col-md-6">
            <FormGroup label="City" required>
              <Input value={address?.city} onChange={onChange("city")} />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup label="Province">
              <Input
                value={address?.province}
                onChange={onChange("province")}
              />
            </FormGroup>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <FormGroup label="Country" required>
              <CountrySelector
                value={address?.country}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  onChange("country")(e.target.value)
                }
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup label="Postal Code" required>
              <Input value={address?.zip} onChange={onChange("zip")} />
            </FormGroup>
          </div>
        </div>
      </Modal>
    </>
  );
}

function GiftProductSelectorModal({
  visible,
  gift,
  toggle,
  selectedProducts,
  onChange,
}: {
  visible: boolean;
  gift: any;
  toggle: () => void;
  selectedProducts: string[];
  onChange: (id: string) => () => void;
}) {
  const website = useSelector((state: any) => state.settings.website);
  const isAnyQuantityMoreThanOne = gift.products?.some(
    (i: any) => i.quantity > 1,
  );
  const hasAllSelected =
    gift.maxProducts && selectedProducts.length >= Number(gift.maxProducts);
  return (
    <Modal
      title={<FormattedMessage id="Choose Products" />}
      visible={visible}
      onClose={toggle}
    >
      <p>
        <FormattedMessage
          id="free_gift_choose"
          defaultMessage={`Choose {num, plural, one {a free product} other {{num} free products}}`}
          values={{ num: gift.maxProducts }}
        />
      </p>
      <ul className="list-group list-group-flush">
        {gift.products?.map((i: any) => {
          const url = `https://${website}/products/${i.handle}`;
          const isSelected = selectedProducts.includes(i._id);
          const isHalfOpacity = !isSelected && hasAllSelected;
          return (
            <li
              key={i._id}
              className={`list-group-item py-3 space-between ${
                isHalfOpacity ? "opacity-50" : "list-group-item-action"
              }`}
              onClick={onChange(i._id)}
            >
              <div className="d-flex align-items-start gap-2">
                <img
                  src={i.images?.[0]?.url}
                  alt=""
                  className="rounded border shadow-sm"
                  style={{ width: 64, height: 64, objectFit: "cover" }}
                />
                <div>
                  <div className="mb-0">{i.name}</div>
                  <small>
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm d-inline-flex align-items-center gap-1 text-decoration-underline"
                    >
                      <FormattedMessage id="View details" /> <FiExternalLink />
                    </a>
                  </small>
                  {isAnyQuantityMoreThanOne && (
                    <div className="mt-2">
                      <span className="text-muted">
                        <FormattedMessage id="Quantity" />: {i.quantity}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <Checkbox checked={isSelected} />
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

function GiftProductChooser({
  selectedProducts,
  gift,
  onChange,
}: {
  selectedProducts: string[];
  gift: any;
  onChange: (id: string) => () => void;
}) {
  const [visible, toggle] = useToggle();
  const needsSelection =
    gift.maxProducts && Number(gift.maxProducts) < gift.products?.length;
  if (!needsSelection) return null;
  return (
    <>
      <button onClick={toggle} className="btn btn-link btn-sm">
        <FormattedMessage id="Choose products" />
      </button>
      <GiftProductSelectorModal
        visible={visible}
        toggle={toggle}
        selectedProducts={selectedProducts}
        gift={gift}
        onChange={onChange}
      />
    </>
  );
}

function CampaignGiftRedeemModal({
  gift,
  onClose,
  campaign_key,
  onMetadataUpdate,
}: {
  gift: any;
  onClose: () => void;
  campaign_key: string;
  onMetadataUpdate: (metadata: any) => void;
}) {
  const { data, isLoading } = useSWRImmutable(
    `/campaigns/seeding/contact_information?campaign_key=${campaign_key}`,
    get,
  );
  const address = data?.address || {};
  const website = useSelector((state: any) => state.settings.website);

  const [customer, setCustomer] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [addressObj, setAddressObj] = useState({
    address1: "",
    address2: "",
    city: "",
    province: "",
    zip: "",
    country: "",
  });

  const onCustomerChange = (name: string) => (value: any) =>
    setCustomer((prev) => ({ ...prev, [name]: value }));
  const onAddressChange = (name: string) => (value: any) =>
    setAddressObj((prev) => ({ ...prev, [name]: value }));

  useEffect(() => {
    setCustomer({
      first_name: data?.first_name ?? "",
      last_name: data?.last_name ?? "",
      email: data?.email ?? "",
      phone: data?.phone ?? "",
    });
    setAddressObj({
      address1: address?.address1 ?? "",
      address2: address?.address2 ?? "",
      city: address?.city ?? "",
      province: address?.province ?? "",
      zip: address?.zip ?? "",
      country: address?.country ?? "",
    });
  }, [data]);

  const needsSelection =
    gift.maxProducts && Number(gift.maxProducts) < gift.products?.length;

  const [selectedProducts, setSelectedProducts] = useState<string[]>(() =>
    needsSelection ? [] : (gift.products?.map((i: any) => i._id) ?? []),
  );
  const [selectedVariations, setSelectedVariations] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (needsSelection) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(gift.products?.map((i: any) => i._id) ?? []);
    }
  }, [needsSelection, gift]);

  const toggleSelectedProduct = (id: string) => () => {
    setSelectedProducts((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id).slice(0, Number(gift.maxProducts));
      } else {
        return [...prev, id].slice(0, Number(gift.maxProducts));
      }
    });
  };

  const isAnyQuantityMoreThanOne = gift.products?.some(
    (i: any) => i.quantity > 1,
  );

  const redeemErrors = (): string[] => {
    const errors: string[] = [];
    if (selectedProducts.length === 0)
      errors.push("Please choose products to redeem");
    else if (
      needsSelection &&
      selectedProducts.length < Number(gift.maxProducts)
    )
      errors.push("Please choose more products to redeem");
    if (!customer.first_name) errors.push("Please enter first name");
    if (!customer.last_name) errors.push("Please enter last name");
    if (!customer.email) errors.push("Please enter email");
    if (!customer.phone) errors.push("Please enter phone");
    if (!addressObj.address1) errors.push("Please enter address");
    if (!addressObj.city) errors.push("Please enter city");
    if (!addressObj.country) errors.push("Please enter country");
    if (!addressObj.zip) errors.push("Please enter postal code");
    return errors;
  };

  const errors = redeemErrors();
  const [redeeming, setRedeeming] = useState(false);

  const redeem = () => {
    setRedeeming(true);
    post(`/campaigns/seeding/redeem?campaign_key=${campaign_key}`, {
      giftId: gift.id,
      shippingAddress: addressObj,
      customer,
      selectedProducts,
      selectedVariations,
    }).then((res: any) => {
      setRedeeming(false);
      if (res.error) {
        alert(res.error);
      } else {
        onMetadataUpdate(res.metadata);
        onClose();
      }
    });
  };

  return (
    <Modal visible title={gift.title} onClose={onClose} size="lg">
      <div className="row g-2 mb-2">
        {/* Product column */}
        <div className="col-md-6">
          <div className="card">
            <div className="pt-2 ps-3">
              <div className="d-flex align-items-center justify-content-between">
                <div className="card-title">
                  <FormattedMessage id="Product Information" />
                </div>
                {selectedProducts.length > 0 && (
                  <GiftProductChooser
                    selectedProducts={selectedProducts}
                    onChange={toggleSelectedProduct}
                    gift={gift}
                  />
                )}
              </div>
            </div>
            {selectedProducts.length === 0 && (
              <div className="card-body text-center">
                <div className="text-muted">
                  <FormattedMessage id="Please choose products to redeem" />
                </div>
                <GiftProductChooser
                  selectedProducts={selectedProducts}
                  onChange={toggleSelectedProduct}
                  gift={gift}
                />
              </div>
            )}
            <ul className="list-group list-group-flush">
              {selectedProducts.map((id) => {
                const item = gift.products?.find((i: any) => i._id === id);
                if (!item) return null;
                const url = `https://${website}/products/${item.handle}`;
                const image = item.images?.[0]?.url;
                const chosenVariation =
                  item.variationOption === "any"
                    ? selectedVariations[item._id]
                    : item.variationOption;
                return (
                  <div
                    key={item._id}
                    className="list-group-item d-flex position-relative space-between py-3"
                  >
                    {needsSelection && (
                      <button
                        onClick={() =>
                          setSelectedProducts((prev) =>
                            prev.filter((i) => i !== id),
                          )
                        }
                        className="position-absolute btn"
                        style={{ top: 0, right: 0 }}
                      >
                        <FiX />
                      </button>
                    )}
                    <div className="d-flex align-items-start gap-2">
                      <img
                        src={image}
                        alt=""
                        className="rounded border shadow-sm"
                        style={{ width: 64, height: 64, objectFit: "cover" }}
                      />
                      <div>
                        <div className="mb-0">{item.name}</div>
                        <small>
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm d-inline-flex align-items-center gap-1 text-decoration-underline"
                          >
                            <FormattedMessage id="View details" />{" "}
                            <FiExternalLink />
                          </a>
                        </small>
                        <div className="mt-2">
                          {isAnyQuantityMoreThanOne && (
                            <div>
                              <span className="text-muted">
                                <FormattedMessage id="Quantity" />:{" "}
                                {item.quantity}
                              </span>
                            </div>
                          )}
                          {item.variationOption === "any" &&
                          item.variations?.length > 1 ? (
                            <div style={{ width: "200px" }}>
                              <Select
                                defaultSelectFirst
                                value={selectedVariations[item._id]}
                                onChange={(value: string) =>
                                  setSelectedVariations((prev) => ({
                                    ...prev,
                                    [item._id]: value,
                                  }))
                                }
                                options={item.variations.map((v: any) => ({
                                  value: v.id,
                                  label: v.name,
                                }))}
                              />
                            </div>
                          ) : (
                            <span>
                              {
                                item.variations?.find(
                                  (v: any) => v.id === chosenVariation,
                                )?.name
                              }
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Contact + Address column */}
        <div className="col-md-6">
          <div className="card">
            <GiftContactInformation
              data={customer}
              onChange={onCustomerChange}
            />
          </div>
          <div className="card mt-2">
            <GiftShippingAddress
              address={addressObj}
              onChange={onAddressChange}
            />
          </div>
        </div>
      </div>

      {!isLoading && errors.length > 0 ? (
        <div className="mt-2 alert alert-warning">
          <ol className="mb-0">
            {errors.map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ol>
        </div>
      ) : (
        <div className="text-center mt-2">
          <StyledButton
            onClick={redeem}
            loading={redeeming}
            disabled={errors.length > 0}
          >
            <FormattedMessage id="Redeem" />
          </StyledButton>
        </div>
      )}
    </Modal>
  );
}

export default function CampaignGiftsCard({
  gifts,
  campaign_key,
  initialMetadata,
}: {
  gifts: any[];
  campaign_key: string;
  initialMetadata?: any;
}) {
  const [parsedMetadata, setParsedMetadata] = useState<any>(
    () => initialMetadata ?? {},
  );
  const giftRedemptions: Array<{
    giftId: string;
    statusPageUrl: string;
    name: string;
  }> = parsedMetadata.gift_redemptions || [];

  const [redeemingGift, setRedeemingGift] = useState<any | null>(null);

  if (!gifts || gifts.length === 0) return null;

  return (
    <div className="card mb-3">
      <div className="card-header fw-semibold">
        <FormattedMessage id="Gifts" />
      </div>
      <ul className="list-group list-group-flush">
        {gifts.map((gift) => {
          const redemption = giftRedemptions.find((r) => r.giftId === gift.id);
          const image =
            gift.products?.length > 0 && gift.products[0]?.images?.[0]?.url;
          return (
            <li key={gift.id} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div className="d-flex gap-3">
                  <img
                    src={image || "https://placehold.co/64"}
                    alt=""
                    style={{ width: 64, height: 64, objectFit: "cover" }}
                    className="rounded border shadow-sm"
                  />
                  <div>
                    <h5 className="mb-0">{gift.title}</h5>
                    {gift.maxProducts && (
                      <div className="text-muted small">
                        {Number(gift.maxProducts) <
                        (gift.products?.length ?? 0) ? (
                          <FormattedMessage
                            id="free_gift_choose"
                            defaultMessage={`Choose {num, plural, one {a free product} other {{num} free products}}`}
                            values={{ num: gift.maxProducts }}
                          />
                        ) : (
                          <FormattedMessage
                            id="free_gift_all"
                            defaultMessage={`Enjoy {num, plural, one {a free product} other {{num} free products}}`}
                            values={{ num: gift.maxProducts }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="d-flex flex-column align-items-end gap-2">
                  {redemption ? (
                    <>
                      <span className="badge badge-success">
                        <FormattedMessage id="Redeemed" />
                      </span>
                      <a
                        href={redemption.statusPageUrl}
                        className="d-inline-flex align-items-center gap-1 small"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FormattedMessage id="Track" />{" "}
                        <FiExternalLink size={12} />
                      </a>
                    </>
                  ) : (
                    <button
                      className="btn btn-link btn-sm"
                      onClick={() => setRedeemingGift(gift)}
                    >
                      <FormattedMessage id="Redeem" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {redeemingGift && (
        <CampaignGiftRedeemModal
          gift={redeemingGift}
          onClose={() => setRedeemingGift(null)}
          campaign_key={campaign_key}
          onMetadataUpdate={setParsedMetadata}
        />
      )}
    </div>
  );
}
